"use client";

import { ActionBarPrimitive, useAuiState } from "@assistant-ui/react";
import { AlertCircleIcon, RefreshCwIcon } from "lucide-react";
import type { FC } from "react";
import { Button } from "@/components/ui/atoms/button";
import { parseChatError } from "@/lib/chat-steps";

/**
 * What went wrong with an answer, in words ("Too many requests", "The model's provider is
 * down"), with a "Try again" button that regenerates it. A stopped answer gets a quiet line and
 * the same button. The ChatAgent tags each error with a code (`turn-metadata.ts` in the API).
 */
export const MessageError: FC = () => {
  const status = useAuiState((s) => s.message.status);
  if (status?.type !== "incomplete") return null;

  if (status.reason === "cancelled") {
    return (
      <div
        data-slot="aui_message-stopped"
        className="text-muted-foreground mt-2 flex items-center gap-2 text-sm"
      >
        <span>Stopped</span>
        <RetryButton label="Retry" />
      </div>
    );
  }
  if (status.reason !== "error") return null;

  const error = parseChatError(status.error);
  return (
    <div
      role="alert"
      data-slot="aui_message-error"
      data-code={error.code}
      className="border-destructive/40 bg-destructive/5 mt-3 flex items-start gap-3 border px-3 py-2.5 text-sm"
    >
      <AlertCircleIcon className="text-destructive mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-foreground font-medium">{error.title}</p>
        <p className="text-muted-foreground line-clamp-3">{error.detail}</p>
      </div>
      {error.retry && <RetryButton label="Try again" />}
    </div>
  );
};

const RetryButton: FC<{ label: string }> = ({ label }) => (
  <ActionBarPrimitive.Reload asChild>
    <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1.5 px-2.5 text-xs">
      <RefreshCwIcon className="size-3.5" />
      {label}
    </Button>
  </ActionBarPrimitive.Reload>
);
